import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

interface LandingPageData {
  id: string;
  name: string;
  content_data: any;
  referrer_message: string;
  tenant_id: string;
  tenants?: {
    name: string;
    domain: string | null;
  };
}

interface ReferrerData {
  full_name: string;
}

export default function PublicLandingPage() {
  const { tenantSlug, pageSlug } = useParams();
  const [searchParams] = useSearchParams();
  const referrerCode = searchParams.get('ref');
  
  const [landingPage, setLandingPage] = useState<LandingPageData | null>(null);
  const [referrer, setReferrer] = useState<ReferrerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  useEffect(() => {
    fetchLandingPageData();
  }, [tenantSlug, pageSlug]);

  useEffect(() => {
    if (referrerCode) {
      fetchReferrerData();
    }
  }, [referrerCode]);

  const fetchLandingPageData = async () => {
    if (!tenantSlug || !pageSlug) return;

    try {
      // First try to find tenant by slug, then by ID if it's a UUID
      let actualTenantSlug = tenantSlug;
      
      // Check if tenantSlug is actually a UUID (tenant ID)
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tenantSlug);
      
      if (isUUID) {
        // Look up tenant slug by ID
        const { data: tenant } = await supabase
          .from('tenants')
          .select('slug')
          .eq('id', tenantSlug)
          .single();
        
        if (tenant) {
          actualTenantSlug = tenant.slug;
        }
      }

      // Use the new secure validation function
      const { data, error } = await supabase.rpc('validate_secure_landing_page_access', {
        tenant_slug: actualTenantSlug,
        page_slug: pageSlug,
        referrer_code: referrerCode,
        user_agent: navigator.userAgent
      });

      if (error) {
        console.error('Error validating access:', error);
        toast.error('Fehler beim Laden der Seite');
        return;
      }

      if (!data || !Array.isArray(data) || data.length === 0 || !data[0].allowed) {
        toast.error('Diese Seite ist nicht verfügbar oder Zugriff verweigert');
        return;
      }

      const result = data[0];
      const pageData = result.page_data;

      if (!pageData) {
        toast.error('Landing Page nicht gefunden');
        return;
      }

      // Get tenant info via secure RPC (avoids RLS/500)
      const { data: tenantInfo, error: tenantErr } = await supabase.rpc('get_tenant_info_secure', {
        tenant_uuid: result.tenant_id
      });
      if (tenantErr) {
        console.warn('Tenant info RPC error:', tenantErr);
      }

      const tenant = tenantInfo as any;

      setLandingPage({
        ...(pageData as any),
        tenant_id: result.tenant_id,
        tenants: tenant || undefined
      });
    } catch (error) {
      console.error('Error fetching landing page:', error);
      toast.error('Fehler beim Laden der Seite');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrerData = async () => {
    if (!referrerCode) return;

    try {
      const { data: resolved, error } = await supabase.functions.invoke('resolve-referral-tenant', {
        body: { referral_code: referrerCode }
      });

      if (!error && resolved) {
        const r: any = resolved;
        if (r.customer_name) setReferrer({ full_name: r.customer_name });
        // Store for submit via closure by reading fresh again if needed
      }
    } catch (error) {
      console.error('Error fetching referrer via edge function:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.email || !landingPage) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    setSubmitting(true);

    try {
      // Resolve referrer via edge function
      let referrerCustomerId = null;
      if (referrerCode) {
        const { data: resolved } = await supabase.functions.invoke('resolve-referral-tenant', {
          body: { referral_code: referrerCode }
        });
        if (resolved && (resolved as any).customer_id) {
          referrerCustomerId = (resolved as any).customer_id;
        }
      }

      // Submit lead via secure RPC to avoid RLS issues
      const { data: result, error } = await supabase.rpc('create_referral_lead_secure' as any, {
        p_tenant_id: landingPage.tenant_id,
        p_first_name: formData.first_name,
        p_email: formData.email,
        p_referrer_customer_id: referrerCustomerId,
        p_referrer_code: referrerCode,
        p_last_name: formData.last_name,
        p_phone: formData.phone,
        p_message: formData.message,
        p_source_data: {
          company: formData.company,
          landing_page_slug: pageSlug,
          tenant_slug: tenantSlug
        }
      });

      if (error) {
        console.error('RPC error:', error);
        throw error;
      }

      const leadResult = result as any;
      if (leadResult && typeof leadResult === 'object' && 'success' in leadResult && !leadResult.success) {
        console.error('Lead creation failed:', leadResult);
        throw new Error(String(leadResult.error || 'Lead creation failed'));
      }

      if (error) throw error;

      setSubmitted(true);
      toast.success('Vielen Dank! Ihre Anfrage wurde erfolgreich übermittelt.');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Fehler beim Übermitteln der Anfrage');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Seite wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!landingPage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Seite nicht gefunden</h1>
          <p className="text-muted-foreground">Die angeforderte Landing Page konnte nicht gefunden werden.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="text-center py-8">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Vielen Dank!</h2>
            <p className="text-muted-foreground mb-4">
              Ihre Anfrage wurde erfolgreich übermittelt. Wir werden uns in Kürze bei Ihnen melden.
            </p>
            {referrer && (
              <p className="text-sm text-muted-foreground">
                Empfohlen von {referrer.full_name}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Extract template data
  const templateData = landingPage.content_data || {};
  const components = templateData.components || [];
  
  // Find hero component
  const heroComponent = components.find((comp: any) => comp.type === 'hero');
  const formComponent = components.find((comp: any) => comp.type === 'form');
  const benefitsComponent = components.find((comp: any) => comp.type === 'benefits');

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 to-primary/5 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Referrer Message */}
          {referrer && (
            <div className="bg-white/80 backdrop-blur-sm border border-primary/20 rounded-lg p-4 mb-8 mx-auto max-w-md">
              <p className="text-lg font-medium text-primary">
                {landingPage.referrer_message.replace('{referrer_name}', referrer.full_name)}
              </p>
            </div>
          )}

          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {heroComponent?.title || 'Willkommen'}
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            {heroComponent?.subtitle || 'Erfahren Sie mehr über unser Angebot'}
          </p>
          
          <div className="text-sm text-muted-foreground mb-4">
            Präsentiert von <span className="font-medium">{landingPage.tenants?.name}</span>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      {benefitsComponent && benefitsComponent.items?.length > 0 && (
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              {benefitsComponent.title || 'Ihre Vorteile'}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {benefitsComponent.items.map((benefit: any, index: number) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ArrowRight className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Form Section */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">
                {formComponent?.title || 'Kontakt aufnehmen'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Vorname *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Nachname</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </div>

                {formComponent?.fields?.includes('company') && (
                  <div>
                    <Label htmlFor="company">Unternehmen</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="message">Nachricht</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    rows={4}
                    placeholder="Teilen Sie uns Ihr Anliegen mit..."
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={submitting}
                >
                  {submitting ? 'Wird gesendet...' : (heroComponent?.ctaText || 'Anfrage senden')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}